import { Simulator } from '../simulation/simulator.ts';
import { PRESETS, PRESET_DESCRIPTIONS, ruleToString } from '../simulation/rules.ts';
import { CanvasRenderer } from '../rendering/canvas-renderer.ts';

export class Controls {
  private sim: Simulator;
  private renderer: CanvasRenderer;

  private ruleInput!: HTMLInputElement;
  private ruleStatus!: HTMLElement;
  private presetSelect!: HTMLSelectElement;
  private playBtn!: HTMLButtonElement;
  private speedSlider!: HTMLInputElement;
  private speedDisplay!: HTMLElement;
  private genDisplay!: HTMLElement;
  private aliveDisplay!: HTMLElement;
  private densityDisplay!: HTMLElement;
  private currentRuleDisplay!: HTMLElement;
  private densitySlider!: HTMLInputElement;
  private widthInput!: HTMLInputElement;
  private heightInput!: HTMLInputElement;

  private ruleGridButtons: Map<number, HTMLElement> = new Map();
  private ruleGridType: Map<number, 'birth' | 'survival'> = new Map();

  private isUpdatingUI = false;

  constructor(sim: Simulator, renderer: CanvasRenderer) {
    this.sim = sim;
    this.renderer = renderer;
  }

  init(): void {
    this.ruleInput = document.getElementById('rule-input') as HTMLInputElement;
    this.ruleStatus = document.getElementById('rule-status') as HTMLElement;
    this.presetSelect = document.getElementById('preset-select') as HTMLSelectElement;
    this.playBtn = document.getElementById('play-btn') as HTMLButtonElement;
    this.speedSlider = document.getElementById('speed-slider') as HTMLInputElement;
    this.speedDisplay = document.getElementById('speed-display') as HTMLElement;
    this.genDisplay = document.getElementById('gen-display') as HTMLElement;
    this.aliveDisplay = document.getElementById('alive-display') as HTMLElement;
    this.densityDisplay = document.getElementById('density-display') as HTMLElement;
    this.currentRuleDisplay = document.getElementById('current-rule-display') as HTMLElement;
    this.densitySlider = document.getElementById('density-slider') as HTMLInputElement;
    this.widthInput = document.getElementById('grid-width') as HTMLInputElement;
    this.heightInput = document.getElementById('grid-height') as HTMLInputElement;

    this.initRuleGrid();
    this.wireAllControls();
    this.syncUIFromSimulator();

    this.sim.onTick = () => this.render();
  }

  private initRuleGrid(): void {
    const birthGrid = document.getElementById('birth-grid') as HTMLElement;
    const survivalGrid = document.getElementById('survival-grid') as HTMLElement;

    birthGrid.innerHTML = '';
    survivalGrid.innerHTML = '';

    const neighborPositions = [0, 1, 2, 3, 4, 5, 6, 7, 8];

    for (const num of neighborPositions) {
      const birthBtn = this.createRuleButton(num, 'birth');
      birthGrid.appendChild(birthBtn);

      const survivalBtn = this.createRuleButton(num, 'survival');
      survivalGrid.appendChild(survivalBtn);
    }
  }

  private createRuleButton(num: number, type: 'birth' | 'survival'): HTMLElement {
    const btn = document.createElement('button');
    btn.className = 'rule-grid-btn';
    btn.textContent = num.toString();
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleRuleNumber(type, num);
    });

    const key = type === 'birth' ? num : num + 100;
    this.ruleGridButtons.set(key, btn);
    this.ruleGridType.set(key, type);

    return btn;
  }

  private toggleRuleNumber(type: 'birth' | 'survival', num: number): void {
    if (this.isUpdatingUI) return;

    const rule = this.sim.rule;
    const set = type === 'birth' ? rule.birth : rule.survival;

    if (set.has(num)) {
      set.delete(num);
    } else {
      set.add(num);
    }

    this.applyRuleChange();
  }

  private applyRuleChange(): void {
    this.isUpdatingUI = true;

    const ruleStr = ruleToString(this.sim.rule);
    this.ruleInput.value = ruleStr;
    this.ruleStatus.textContent = 'Valid';
    this.ruleStatus.className = 'rule-status valid';
    this.presetSelect.value = '';

    this.syncUIFromSimulator();

    this.isUpdatingUI = false;
  }

  private wireAllControls(): void {
    this.wireRuleInput();
    this.wirePresetSelect();
    this.wirePlayback();
    this.wireGridControls();
    this.wireRenderer();
  }

  private wireRenderer(): void {
    this.renderer.onCellToggle = (x, y, value) => {
      const grid = this.sim.grid;
      if (x >= 0 && x < grid.width && y >= 0 && y < grid.height) {
        grid.set(x, y, value);
        this.render();
      }
    };
  }

  private wireRuleInput(): void {
    this.ruleInput.addEventListener('input', () => {
      if (this.isUpdatingUI) return;

      const value = this.ruleInput.value;
      const ok = this.sim.setRule(value);

      this.isUpdatingUI = true;
      this.ruleStatus.textContent = ok ? 'Valid' : 'Invalid';
      this.ruleStatus.className = 'rule-status ' + (ok ? 'valid' : 'invalid');

      if (ok) {
        this.presetSelect.value = '';
        this.syncUIFromSimulator();
      }
      this.isUpdatingUI = false;
    });
  }

  private wirePresetSelect(): void {
    for (const [name] of Object.entries(PRESETS)) {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = name;
      this.presetSelect.appendChild(option);
    }

    this.presetSelect.addEventListener('change', () => {
      if (this.isUpdatingUI) return;

      const name = this.presetSelect.value;
      if (!name) return;

      const rule = PRESETS[name];
      this.sim.setRule(rule);

      this.isUpdatingUI = true;
      const desc = PRESET_DESCRIPTIONS[name] || '';
      document.getElementById('preset-description')!.textContent = desc;
      this.syncUIFromSimulator();
      this.isUpdatingUI = false;
    });
  }

  private wirePlayback(): void {
    this.playBtn.addEventListener('click', () => {
      this.sim.togglePlay();
      this.updatePlayButton();
    });

    document.getElementById('step-btn')!.addEventListener('click', () => {
      if (this.sim.playing) this.sim.pause();
      this.sim.step();
      this.updatePlayButton();
      this.render();
    });

    document.getElementById('reset-btn')!.addEventListener('click', () => {
      this.sim.reset();
      this.updatePlayButton();
      this.render();
    });

    this.speedSlider.addEventListener('input', () => {
      const val = parseInt(this.speedSlider.value);
      this.sim.setSpeed(val);
      this.speedDisplay.textContent = val + ' gen/s';
    });
  }

  private wireGridControls(): void {
    document.getElementById('randomize-btn')!.addEventListener('click', () => {
      const density = parseFloat(this.densitySlider.value);
      this.sim.randomize(density);
      this.render();
    });

    document.getElementById('clear-btn')!.addEventListener('click', () => {
      this.sim.reset();
      this.updatePlayButton();
      this.render();
    });

    this.densitySlider.addEventListener('input', () => {
      const label = document.getElementById('density-label');
      if (label) {
        label.textContent = Math.round(parseFloat(this.densitySlider.value) * 100) + '%';
      }
    });

    const applySize = () => {
      const w = parseInt(this.widthInput.value) || 100;
      const h = parseInt(this.heightInput.value) || 100;
      this.sim.resize(w, h);
      this.renderer.centerOn(w, h);
      this.render();
    };

    this.widthInput.addEventListener('change', applySize);
    this.heightInput.addEventListener('change', applySize);
  }

  private syncUIFromSimulator(): void {
    const rule = this.sim.rule;
    const ruleStr = ruleToString(rule);

    // Update textfield to match simulator state
    this.ruleInput.value = ruleStr;
    this.ruleStatus.textContent = 'Valid';
    this.ruleStatus.className = 'rule-status valid';

    // Update grid buttons to match simulator state
    for (let i = 0; i <= 8; i++) {
      const birthKey = i;
      const survivalKey = i + 100;

      const birthBtn = this.ruleGridButtons.get(birthKey);
      const survivalBtn = this.ruleGridButtons.get(survivalKey);

      if (birthBtn) {
        birthBtn.classList.toggle('active', rule.birth.has(i));
      }
      if (survivalBtn) {
        survivalBtn.classList.toggle('active', rule.survival.has(i));
      }
    }

    // Update display
    this.currentRuleDisplay.textContent = ruleStr;
    this.updateStats();
  }

  private updatePlayButton(): void {
    this.playBtn.textContent = this.sim.playing ? 'Pause' : 'Play';
  }

  private updateStats(): void {
    const grid = this.sim.grid;
    const alive = grid.countAlive();
    const total = grid.width * grid.height;

    this.genDisplay.textContent = this.sim.generation.toString();
    this.aliveDisplay.textContent = alive.toString();
    this.densityDisplay.textContent = total > 0
      ? (alive / total * 100).toFixed(1) + '%'
      : '0%';
  }

  render(): void {
    this.renderer.render(this.sim.grid);
    this.updateStats();
  }
}
